import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LoadingState, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Database, Plus, Trash2, Sparkles, Loader2, X, Wand2, Check, FileUp, FileDown, Eye, Terminal, ChevronDown, ChevronRight, Layers } from "lucide-react";

const DIFF = {
  easy: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  hard: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};
const TYPES = ["scenario", "technical", "situational", "work_sample", "command_lab", "behavioral"];
const TYPE_LABEL = { scenario: "Scenario", technical: "Technical", situational: "Situational", work_sample: "Work sample", command_lab: "Command lab", behavioral: "Behavioral" };
const blank = { competency: "", difficulty: "medium", type: "scenario", prompt: "", assesses: "", tags: "" };

export default function QBank() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiCount, setAiCount] = useState(5);
  const [aiBusy, setAiBusy] = useState(false);
  const [drafts, setDrafts] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const parseRows = (text) => {
    const rows = [];
    text.split(/\r?\n/).forEach((lnRaw, i) => {
      const ln = lnRaw.trim();
      if (!ln) return;
      const cols = ln.split(",").map((s) => s.trim());
      if (i === 0 && cols[0]?.toLowerCase() === "competency") return; // header row
      if (cols.length === 1) { rows.push({ competency: "General", difficulty: "medium", type: "scenario", prompt: cols[0], assesses: "", tags: [] }); return; }
      const [competency, difficulty, prompt, assesses, tags] = cols;
      if (!prompt) return;
      rows.push({
        competency: competency || "General",
        difficulty: ["easy", "medium", "hard"].includes((difficulty || "").toLowerCase()) ? difficulty.toLowerCase() : "medium",
        type: "scenario", prompt, assesses: assesses || "",
        tags: tags ? tags.split(/[;|]/).map((t) => t.trim()).filter(Boolean) : [],
      });
    });
    return rows;
  };
  const onFile = (e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = () => setRaw(String(r.result || "")); r.readAsText(f); };
  const downloadTemplate = () => {
    const csv = "competency,difficulty,prompt,assesses,tags\nSystem Design,hard,Design a rate limiter for a public API,Scalability,backend;scalability\nCommunication,easy,Explain a technical concept to a non-expert,Clarity,communication\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "qbank-template.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const doImport = async () => {
    const rows = parseRows(raw);
    if (!rows.length) { toast.error("No valid rows found — check the format"); return; }
    setImportBusy(true);
    try { const { data } = await api.post("/qbank/bulk", { questions: rows }); toast.success(`Imported ${data.added} question${data.added === 1 ? "" : "s"}`); setImportOpen(false); setRaw(""); load(); }
    catch { toast.error("Import failed"); }
    finally { setImportBusy(false); }
  };

  const genAi = async () => {
    if (!aiTopic.trim()) { toast.error("Describe a skill or topic"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/qbank/generate", { topic: aiTopic.trim(), count: aiCount });
      setDrafts((data.questions || []).map((q) => ({ ...q, _keep: true })));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not draft questions"); }
    finally { setAiBusy(false); }
  };
  const saveDrafts = async () => {
    const keep = (drafts || []).filter((d) => d._keep);
    if (!keep.length) { toast.error("Select at least one question"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/qbank/bulk", { questions: keep });
      toast.success(`Added ${data.added} question${data.added === 1 ? "" : "s"} to the bank`);
      setAiOpen(false); setDrafts(null); setAiTopic(""); load();
    } catch { toast.error("Could not add questions"); }
    finally { setAiBusy(false); }
  };

  const load = () => { setLoading(true); api.get("/qbank").then((r) => setData(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const add = async () => {
    if (!form.competency.trim() || !form.prompt.trim()) { toast.error("Competency and question are required"); return; }
    setSaving(true);
    try {
      await api.post("/qbank", { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Question added to the bank");
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/qbank/${id}`); setData((d) => ({ ...d, questions: d.questions.filter((q) => q.id !== id) })); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  if (loading) return <LoadingState label="Loading the Question Bank…" />;

  const comps = data?.competencies || [];
  const questions = (data?.questions || []).filter((q) => filter === "all" || q.competency === filter);
  const slug = (s) => (s || "").replace(/\s+/g, "-").toLowerCase();
  const grouped = questions.reduce((acc, q) => { (acc[q.competency] = acc[q.competency] || []).push(q); return acc; }, {});

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6">
      <button onClick={() => nav("/recruiter/jobs")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="qbank-back"><ArrowLeft className="h-4 w-4" /> Jobs</button>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1 flex items-center gap-2"><Database className="h-3.5 w-3.5" /> Question Bank</div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Reusable, fair questions</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">Assessments are composed <span className="text-primary">dynamically from this bank</span> whenever a role has no hand-authored test — matched to the role's competencies, with a balanced difficulty mix.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => { setImportOpen(true); setRaw(""); }} className="gap-1.5" data-testid="qbank-import-open"><FileUp className="h-4 w-4" /> Import</Button>
          <Button variant="outline" onClick={() => { setAiOpen(true); setDrafts(null); }} className="gap-1.5" data-testid="qbank-ai-open"><Wand2 className="h-4 w-4" /> AI author</Button>
          <Button onClick={() => setForm(blank)} className="gap-1.5" data-testid="qbank-add-open"><Plus className="h-4 w-4" /> Add question</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setFilter("all")} data-active={filter === "all"} data-testid="qbank-filter-all"
          className="px-3 py-1 rounded-full text-xs border transition-colors data-[active=true]:bg-primary/15 data-[active=true]:border-primary/40 data-[active=true]:text-primary border-border text-muted-foreground hover:text-foreground">All · {data?.total || 0}</button>
        {comps.map((c) => (
          <button key={c} onClick={() => setFilter(c)} data-active={filter === c} data-testid={`qbank-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
            className="px-3 py-1 rounded-full text-xs border transition-colors data-[active=true]:bg-primary/15 data-[active=true]:border-primary/40 data-[active=true]:text-primary border-border text-muted-foreground hover:text-foreground">{c}</button>
        ))}
      </div>

      {questions.length === 0 ? (
        <EmptyState icon={Database} title="No questions here yet" description="Add role-relevant questions and they'll flow into assessments automatically." action={<Button className="mt-2" onClick={() => setForm(blank)}>Add question</Button>} />
      ) : (
        <div className="space-y-4" data-testid="qbank-list">
          {Object.entries(grouped).map(([comp, items]) => {
            const isCollapsed = !!collapsed[comp];
            const dc = { easy: 0, medium: 0, hard: 0 };
            items.forEach((q) => { dc[q.difficulty] = (dc[q.difficulty] || 0) + 1; });
            return (
              <div key={comp} className="rounded-2xl border border-border/70 bg-card/40 overflow-hidden" data-testid={`qbank-group-${slug(comp)}`}>
                <button onClick={() => setCollapsed((c) => ({ ...c, [comp]: !c[comp] }))} data-testid={`qbank-group-toggle-${slug(comp)}`}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Layers className="h-4 w-4 text-primary" /></div>
                    <div className="text-left min-w-0">
                      <div className="font-display font-semibold truncate">{comp}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{items.length} question{items.length === 1 ? "" : "s"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {["easy", "medium", "hard"].map((d) => dc[d] > 0 && <span key={d} className={`hidden sm:inline text-[10px] px-1.5 py-0.5 rounded border ${DIFF[d]}`}>{dc[d]} {d}</span>)}
                    {isCollapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  </div>
                </button>
                {!isCollapsed && (
                  <div className="p-3 pt-0 grid sm:grid-cols-2 gap-3">
                    {items.map((q) => <QCard key={q.id} q={q} onPreview={() => setPreview(q)} onDelete={() => del(q.id)} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="qbank-import-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setImportOpen(false)} />
          <div className="relative w-full max-w-2xl rounded-2xl border border-border bg-card p-6 space-y-4 max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2"><FileUp className="h-4 w-4 text-primary" /> Bulk import questions</h3>
              <button onClick={() => setImportOpen(false)} data-testid="qbank-import-close"><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground leading-relaxed">
              One question per line as CSV: <code className="text-primary">competency, difficulty, prompt, assesses, tags</code> (difficulty = easy/medium/hard; tags separated by <code>;</code>). A header row is auto-skipped. A single value per line is treated as a plain prompt under "General".
            </div>
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-1.5 text-sm text-primary cursor-pointer hover:underline" data-testid="qbank-import-file">
                <FileUp className="h-4 w-4" /> Upload .csv / .txt
                <input type="file" accept=".csv,.txt" className="hidden" onChange={onFile} />
              </label>
              <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid="qbank-import-template"><FileDown className="h-4 w-4" /> Download template</button>
            </div>
            <Textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={8} placeholder={"System Design, hard, Design a rate limiter for a public API, Scalability, backend;scalability\nCommunication, easy, Explain a technical concept to a non-expert"} data-testid="qbank-import-text" className="font-mono text-xs" />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground" data-testid="qbank-import-count">{raw.trim() ? `${parseRows(raw).length} question(s) detected` : "Paste or upload to preview"}</span>
              <Button onClick={doImport} disabled={importBusy || !raw.trim()} data-testid="qbank-import-submit">{importBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Import to bank"}</Button>
            </div>
          </div>
        </div>
      )}

      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="qbank-ai-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setAiOpen(false)} />
          <div className="relative w-full max-w-2xl rounded-2xl border border-border bg-card p-6 space-y-4 max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Wand2 className="h-4 w-4 text-primary" /> AI question author</h3>
              <button onClick={() => setAiOpen(false)} data-testid="qbank-ai-close"><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <p className="text-sm text-muted-foreground">Describe a skill or role focus — the AI drafts fair, job-relevant questions across difficulties. Review, then add the ones you like.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="e.g. Kubernetes reliability engineering" data-testid="qbank-ai-topic" className="flex-1" onKeyDown={(e) => e.key === "Enter" && genAi()} />
              <Select value={String(aiCount)} onValueChange={(v) => setAiCount(Number(v))}>
                <SelectTrigger className="w-full sm:w-28" data-testid="qbank-ai-count"><SelectValue /></SelectTrigger>
                <SelectContent>{[3, 5, 8, 10].map((n) => <SelectItem key={n} value={String(n)}>{n} Qs</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={genAi} disabled={aiBusy} className="gap-1.5" data-testid="qbank-ai-generate">{aiBusy && !drafts ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Draft</Button>
            </div>
            {drafts && (
              <div className="space-y-2" data-testid="qbank-ai-drafts">
                <div className="text-xs text-muted-foreground">{drafts.filter((d) => d._keep).length} of {drafts.length} selected</div>
                {drafts.map((d, i) => (
                  <button key={i} onClick={() => setDrafts((ds) => ds.map((x, j) => j === i ? { ...x, _keep: !x._keep } : x))} data-testid={`qbank-ai-draft-${i}`}
                    className={`w-full text-left rounded-xl border p-3.5 transition-colors flex items-start gap-3 ${d._keep ? "border-primary/50 bg-primary/[0.06]" : "border-border bg-card"}`}>
                    <div className={`h-5 w-5 rounded flex items-center justify-center shrink-0 mt-0.5 ${d._keep ? "bg-primary text-primary-foreground" : "border border-border"}`}>{d._keep && <Check className="h-3.5 w-3.5" />}</div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">{d.competency}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${DIFF[d.difficulty] || ""}`}>{d.difficulty}</span>
                      </div>
                      <p className="text-sm">{d.prompt}</p>
                    </div>
                  </button>
                ))}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="ghost" onClick={genAi} disabled={aiBusy}>Re-draft</Button>
                  <Button onClick={saveDrafts} disabled={aiBusy} data-testid="qbank-ai-save">{aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Add ${drafts.filter((d) => d._keep).length} to bank`}</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="qbank-form">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Add a question</DialogTitle>
            <DialogDescription className="sr-only">Compose a fair, job-relevant question for your bank.</DialogDescription>
          </DialogHeader>
          {form && (
            <>
            <div className="space-y-3">
              <div><label className="text-xs text-muted-foreground">Competency</label><Input value={form.competency} onChange={(e) => setForm({ ...form, competency: e.target.value })} placeholder="e.g. System Design" data-testid="qbank-competency" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground">Difficulty</label>
                  <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                    <SelectTrigger data-testid="qbank-difficulty"><SelectValue /></SelectTrigger>
                    <SelectContent>{["easy", "medium", "hard"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs text-muted-foreground">Type</label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="qbank-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><label className="text-xs text-muted-foreground">Question</label><Textarea value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} rows={4} placeholder="Write a fair, job-relevant question…" data-testid="qbank-prompt" /></div>
              <div><label className="text-xs text-muted-foreground">Assesses (optional)</label><Input value={form.assesses} onChange={(e) => setForm({ ...form, assesses: e.target.value })} placeholder="e.g. Trade-off analysis" data-testid="qbank-assesses" /></div>
              <div><label className="text-xs text-muted-foreground">Match tags (comma-separated)</label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="engineer, backend, scalability" data-testid="qbank-tags" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
              <Button variant="outline" onClick={() => form.prompt.trim() && setPreview({ ...form, tags: [] })} disabled={!form.prompt.trim()} className="gap-1.5" data-testid="qbank-form-preview"><Eye className="h-4 w-4" /> Preview</Button>
              <Button onClick={add} disabled={saving} data-testid="qbank-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to bank"}</Button>
            </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="qbank-preview-modal">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary"><Eye className="h-3.5 w-3.5" /> Candidate preview</DialogTitle>
            <DialogDescription className="sr-only">Read-only preview of exactly how this item appears to a candidate in the proctored room.</DialogDescription>
          </DialogHeader>
          {preview && (
          <div className="space-y-4">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">{preview.competency || "Competency"}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${DIFF[preview.difficulty] || ""}`}>{preview.difficulty}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{TYPE_LABEL[preview.type] || preview.type}</span>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{preview.type === "command_lab" ? "Scenario brief" : "Question"}</div>
              <p className="text-base leading-relaxed text-foreground/90 whitespace-pre-wrap">{preview.prompt}</p>
              {preview.assesses && <p className="text-xs text-muted-foreground mt-3">Assesses: {preview.assesses}</p>}
            </div>
            {preview.type === "command_lab" ? (
              <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="qbank-preview-lab">
                <div className="flex items-center gap-1.5 px-3 py-2 bg-black/50 border-b border-white/10">
                  <span className="h-2.5 w-2.5 rounded-full bg-rose-500/70" /><span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
                  <span className="ml-2 text-[10px] font-mono text-muted-foreground inline-flex items-center gap-1"><Terminal className="h-3 w-3" /> candidate-lab — bash</span>
                </div>
                <div className="bg-black/70 p-4 font-mono text-xs text-emerald-300 min-h-[120px]">
                  <span className="text-muted-foreground">$</span> <span className="animate-pulse">▍</span>
                  <div className="text-muted-foreground/70 mt-2">// The candidate applies commands here to complete the parametric lab task.</div>
                </div>
              </div>
            ) : (
              <Textarea rows={5} disabled placeholder="The candidate writes their structured answer here…" className="bg-white/[0.02]" data-testid="qbank-preview-answer" />
            )}
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-primary" /> Preview only — this is exactly how a candidate sees the item in the proctored room.</div>
          </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QCard({ q, onPreview, onDelete }) {
  return (
    <div className="group relative rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors flex flex-col" data-testid={`qbank-item-${q.id}`}>
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${DIFF[q.difficulty] || ""}`}>{q.difficulty}</span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">{q.type === "command_lab" && <Terminal className="h-3 w-3 text-primary" />}{TYPE_LABEL[q.type] || (q.type || "").replace(/_/g, " ")}</span>
      </div>
      <p className="text-sm leading-relaxed line-clamp-3">{q.prompt}</p>
      {q.assesses && <p className="text-xs text-muted-foreground mt-2">Assesses: {q.assesses}</p>}
      <div className="mt-3 pt-3 border-t border-border/50 flex items-center gap-1">
        <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" onClick={onPreview} data-testid={`qbank-preview-${q.id}`}><Eye className="h-3.5 w-3.5" /> Preview</Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto text-muted-foreground hover:text-rose-400" onClick={onDelete} data-testid={`qbank-del-${q.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
}
